import { useEffect, useMemo, useRef } from 'react'
import { ReactRenderer } from '@tiptap/react'
import type { SuggestionProps } from '@tiptap/suggestion'
import {
  computePosition,
  autoUpdate,
  flip,
  shift,
  offset,
} from '@floating-ui/dom'
import type { MentionUser } from 'types/discussion'
import { MentionList } from './index'
import type { MentionListHandle } from './index'
import { filterUsers } from './utils'
import type { MentionMenu } from '../../types'

function buildMentionListProps(
  props: SuggestionProps<MentionUser>,
  users: MentionUser[],
  loading: boolean,
  onInvite?: (query: string) => void,
) {
  return {
    ...props,
    items: filterUsers(users, props.query),
    loading,
    onInvite,
  }
}

/**
 * Mention settings that draw the Radix mention popup. Only the web entry
 * (`../../web`) may call this: it imports Radix, which must stay out of the
 * mobile bundle.
 */
export function useWebMentionMenu(
  users: MentionUser[],
  usersLoading: boolean,
  onInvite?: (query: string) => void,
) {
  const usersRef = useRef(users)
  usersRef.current = users
  const usersLoadingRef = useRef(usersLoading)
  usersLoadingRef.current = usersLoading
  const onInviteRef = useRef(onInvite)
  onInviteRef.current = onInvite

  const activePopupRef = useRef<{
    element: HTMLElement | null
    cleanup: (() => void) | null
    component: ReactRenderer<MentionListHandle> | null
    props: SuggestionProps<MentionUser> | null
  }>({ element: null, cleanup: null, component: null, props: null })

  useEffect(() => {
    const { component, props } = activePopupRef.current
    if (!component || !props) return
    const updatedProps = buildMentionListProps(
      props,
      users,
      usersLoading,
      onInviteRef.current,
    )
    component.updateProps(updatedProps)
    activePopupRef.current.props = updatedProps
  }, [users, usersLoading])

  useEffect(() => {
    return () => {
      activePopupRef.current.cleanup?.()
      activePopupRef.current.element?.remove()
      activePopupRef.current.component?.destroy()
    }
  }, [])

  // Empty deps: the editor rebuilds its extensions when this object changes,
  // so it reads the latest values through refs instead.
  return useMemo<MentionMenu>(
    () => ({
      items: ({ query }) => filterUsers(usersRef.current, query),
      render: () => {
        let component: ReactRenderer<MentionListHandle> | null = null
        let element: HTMLElement | null = null
        let cleanup: (() => void) | null = null
        let getRect: (() => DOMRect) | null = null

        function positionEl() {
          if (!element || !getRect) return
          const virtualEl = { getBoundingClientRect: getRect }
          computePosition(virtualEl, element, {
            strategy: 'fixed',
            placement: 'bottom-start',
            middleware: [offset(4), flip(), shift({ padding: 8 })],
          }).then(({ x, y }) => {
            if (!element) return
            element.style.transform = `translate(${Math.round(
              x,
            )}px, ${Math.round(y)}px)`
          })
        }

        function teardown() {
          const componentToDestroy = component
          cleanup?.()
          element?.remove()
          componentToDestroy?.destroy()
          if (activePopupRef.current.component === componentToDestroy) {
            activePopupRef.current = {
              element: null,
              cleanup: null,
              component: null,
              props: null,
            }
          }
          cleanup = null
          element = null
          getRect = null
          component = null
        }

        return {
          onStart: props => {
            teardown()
            if (!props.clientRect) return

            const updatedProps = buildMentionListProps(
              props,
              usersRef.current,
              usersLoadingRef.current,
              onInviteRef.current,
            )
            component = new ReactRenderer(MentionList, {
              props: updatedProps,
              editor: props.editor,
            })
            element = document.createElement('div')
            element.style.position = 'fixed'
            element.style.top = '0'
            element.style.left = '0'
            element.style.zIndex = '9999'
            element.appendChild(component.element)
            // Keep Radix components inside the editor's Theme subtree so they
            // inherit its tokens. Fixed positioning keeps Floating UI's
            // viewport-relative coordinates correct even when this container
            // is nested inside a positioned element.
            const themeContainer =
              props.editor.view.dom.closest('.radix-themes')
            const popupContainer = themeContainer ?? document.body
            popupContainer.appendChild(element)

            getRect = props.clientRect as () => DOMRect
            const virtualEl = { getBoundingClientRect: getRect }

            function handleClickOutside(event: MouseEvent) {
              if (element && !element.contains(event.target as Node)) {
                teardown()
              }
            }
            document.addEventListener('mousedown', handleClickOutside)

            // autoUpdate returns a cleanup function
            const stopAutoUpdate = autoUpdate(virtualEl, element, positionEl)
            cleanup = () => {
              document.removeEventListener('mousedown', handleClickOutside)
              stopAutoUpdate()
            }

            activePopupRef.current = {
              element,
              cleanup,
              component,
              props: updatedProps,
            }
          },

          onUpdate: props => {
            const updatedProps = buildMentionListProps(
              props,
              usersRef.current,
              usersLoadingRef.current,
              onInviteRef.current,
            )
            component?.updateProps(updatedProps)
            activePopupRef.current.props = updatedProps
            if (props.clientRect) {
              getRect = props.clientRect as () => DOMRect
              positionEl()
            }
          },

          onKeyDown: props => {
            if (props.event.key === 'Escape') {
              // Dismiss only the dropdown. Without this the event bubbles to
              // react-modal's close-on-escape and closes the whole modal,
              // losing the draft.
              props.event.stopPropagation()
              teardown()
              return true
            }
            if (props.event.key === 'Tab') {
              // When the empty state offers Invite, move focus onto it (keeping
              // the popup open) so keyboard users can reach it; there are no
              // items to tab through, so tabbing out otherwise closes the popup.
              const inviteButton = element?.querySelector<HTMLButtonElement>(
                '[data-mention-invite]',
              )
              if (inviteButton) {
                props.event.preventDefault()
                inviteButton.focus()
                return true
              }
              teardown()
              return false
            }
            return component?.ref?.onKeyDown(props) ?? false
          },

          onExit: () => {
            teardown()
          },
        }
      },
    }),
    [],
  )
}
